import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMobileBrowserDetection } from '@/hooks/useMobileBrowserDetection';
import { useTranslation } from 'react-i18next';

interface MobileBrowserBlockerProps {
  children: React.ReactNode;
}

const PUBLIC_ROUTES = ['/set-password', '/forgot-password', '/reset-password'];

export function MobileBrowserBlocker({ children }: MobileBrowserBlockerProps) {
  const { t } = useTranslation('appui');
  const { isMobileBrowser } = useMobileBrowserDetection();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Allow public routes and open-app pages
    if (PUBLIC_ROUTES.includes(location.pathname) || location.pathname.startsWith('/open-app')) {
      return;
    }

    // Block mobile browsers
    if (isMobileBrowser) {
      navigate('/open-app/login?redirected=1', { replace: true });
    }
  }, [isMobileBrowser, location.pathname, navigate]);

  // Show loading state while detecting
  if (isMobileBrowser === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">{t('mobileBrowserBlocker.checkingDevice')}</p>
      </div>
    );
  }

  // Block detected - show nothing while redirecting
  if (isMobileBrowser && !PUBLIC_ROUTES.includes(location.pathname) && !location.pathname.startsWith('/open-app')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">{t('mobileBrowserBlocker.redirecting')}</p>
      </div>
    );
  }

  return <>{children}</>;
}
